import { useEffect, useState } from "react";
import { Clock, MapPin, User, Sparkles, ChevronRight, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Iter 460 · Feb 2026 · Karine ask —
 * "Clients should easily be able to book Tanya or Karine and see at a
 * glance the LMC available."
 *
 * Renders two side-by-side (stacked on mobile) provider cards below the
 * hero. Each card shows the provider's next few last-minute openings
 * + a big provider-scoped "Book with X" CTA. The user gets a live
 * pulse of who's open right now without having to open the LMC modal.
 *
 * Props:
 *   onBookProvider(slug) — required. Called with "karine" | "tanya" when
 *                          the client taps a Book CTA. Should route into
 *                          the booking flow with `?provider=` preset.
 *   onOpenLastMinute()  — optional. Opens the full LMC modal (for
 *                          the "See all X openings" link).
 *   onPickLmcSlot(slot) — optional. If provided, tapping any specific
 *                          time chip jumps to Quick Booking pre-filled
 *                          with that exact slot (same contract as
 *                          LastMinuteClubModal onPickSlot).
 */
export default function LastMinuteAtAGlance({ onBookProvider, onOpenLastMinute, onPickLmcSlot, embedded = false }) {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/last-minute/slots?duration=30`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("fetch failed"))))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Bucket every slot into per-provider previews so the two cards can
  // render independently without traversing the tree twice.
  // Iter 461 · Feb 2026 · production crash fix — the previous `x || []`
  // fallback only guarded null/undefined. If the API ever returned a
  // truthy non-array (e.g., `{}` from a cache glitch or 502 body),
  // `.forEach` threw `((intermediate value)... || []).forEach is not a
  // function`, caught by the error reporter and emailed live. Use
  // `Array.isArray` at every step so we degrade gracefully. Wrap the
  // whole compute in a try/catch belt-and-suspenders so any future
  // shape drift falls back to empty buckets instead of crashing the
  // whole home page via the app-wide ErrorBoundary.
  const asArr = (v) => (Array.isArray(v) ? v : []);
  const buckets = { karine: [], tanya: [], linda: [] };
  try {
    asArr(data?.by_day).forEach((day) => {
      asArr(day?.locations).forEach((loc) => {
        asArr(loc?.slots).forEach((slot) => {
          const p = slot?.provider === "tanya"
            ? "tanya"
            : slot?.provider === "linda"
              ? "linda"
              : "karine";
          buckets[p].push({
            ...slot,
            location_id: loc.location_id,
            location_name: loc.name,
            date: day.date,
            weekday: day.weekday,
            month_day: day.month_day,
            duration_minutes: 30,
          });
        });
      });
    });
  } catch (_e) {
    // eslint-disable-next-line no-console
    console.warn("[LastMinuteAtAGlance] bucket compute failed — hiding section", _e);
    buckets.karine = [];
    buckets.tanya = [];
    buckets.linda = [];
  }

  const renderCard = ({ providerKey, providerName, title, accentBg, accentText, accentBorder, accentDot, chipBg, chipBorder, chipText, buttonBg }) => {
    const slots = buckets[providerKey] || [];
    const preview = slots.slice(0, 4);
    return (
      <div
        className={`rounded-2xl border ${accentBorder} ${accentBg} p-4 sm:p-5 flex flex-col gap-3`}
        data-testid={`glance-card-${providerKey}`}
      >
        <div className="flex items-center gap-2">
          <span className={`inline-flex w-9 h-9 rounded-full ${chipBg} ${chipBorder} border items-center justify-center flex-shrink-0`}>
            <User className={`w-4 h-4 ${accentText}`} />
          </span>
          <div className="flex-1 min-w-0">
            <p className={`text-base sm:text-lg font-bold ${accentText} leading-tight`}>
              {providerName}
            </p>
            <p className="text-xs sm:text-[13px] text-zinc-200 font-medium leading-tight">
              {title}
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${chipBg} ${chipText} border ${chipBorder}`}
            data-testid={`glance-count-${providerKey}`}
          >
            <Sparkles className="w-2.5 h-2.5" />
            {slots.length} {isFr ? "ouv." : "spots"}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-3 text-zinc-500 text-xs">
            <Loader2 className="w-3 h-3 animate-spin" />
            {isFr ? "Chargement des disponibilités…" : "Loading availability…"}
          </div>
        ) : preview.length === 0 ? (
          <div className="py-3 text-zinc-500 text-xs" data-testid={`glance-empty-${providerKey}`}>
            {isFr
              ? "Aucune ouverture de dernière minute pour l'instant."
              : "No last-minute spots right now."}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid={`glance-preview-${providerKey}`}>
            {preview.map((slot, i) => (
              <button
                key={`${slot.location_id}-${slot.date}-${slot.start_time}-${i}`}
                type="button"
                onClick={() => onPickLmcSlot?.({ ...slot, provider: providerKey })}
                className={`inline-flex flex-col items-start px-2.5 py-1.5 rounded-lg text-[13px] font-bold border transition-all hover:scale-[1.02] ${chipBg} ${chipBorder} ${chipText} hover:brightness-110`}
                data-testid={`glance-slot-${providerKey}-${slot.date}-${slot.start_time}`}
              >
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3 opacity-90" />
                  {slot.label || slot.start_time}
                </span>
                <span className="text-[11px] opacity-95 flex items-center gap-1 mt-0.5 font-medium">
                  <MapPin className="w-2 h-2" />
                  {slot.weekday || ""} {slot.month_day || ""} · {slot.location_name}
                </span>
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => onBookProvider?.(providerKey)}
          className={`mt-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-lg text-sm font-bold text-white ${buttonBg} shadow-md transition-all active:scale-[0.98]`}
          data-testid={`glance-book-${providerKey}`}
        >
          {isFr ? `Réserver avec ${providerName.split(" ")[0]}` : `Book with ${providerName.split(" ")[0]}`}
          <ChevronRight className="w-4 h-4" />
        </button>

        {slots.length > preview.length && (
          <button
            type="button"
            onClick={onOpenLastMinute}
            className={`text-[13px] font-bold ${accentText} hover:brightness-125 underline underline-offset-2 self-center`}
            data-testid={`glance-see-all-${providerKey}`}
          >
            {isFr
              ? `Voir les ${slots.length} ouvertures →`
              : `See all ${slots.length} openings →`}
          </button>
        )}
      </div>
    );
  };

  if (error) return null; // Silent fail — home page shouldn't break if LMC endpoint hiccups.

  const total = (buckets.karine.length + buckets.tanya.length + buckets.linda.length);
  if (!loading && total === 0 && !embedded) return null; // Nothing to promote — hide entirely.

  return (
    <section
      className={embedded
        ? "mt-3 rounded-2xl bg-zinc-950 border border-zinc-800 p-3"
        : "max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 my-4 rounded-3xl bg-zinc-950 border border-zinc-800 shadow-xl"}
      data-testid={embedded ? "lmc-at-a-glance-embedded" : "lmc-at-a-glance"}
      aria-label={isFr ? "Aperçu des disponibilités de dernière minute" : "Last-minute availability at a glance"}
    >
      {!embedded && <div className="flex items-baseline justify-between mb-3 sm:mb-4">
        <h2
          className="text-white text-xl sm:text-2xl font-semibold leading-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {isFr ? "Réservez maintenant" : "Book now, in one tap"}
        </h2>
        <button
          type="button"
          onClick={onOpenLastMinute}
          className="text-xs sm:text-sm text-amber-400 hover:text-amber-300 font-semibold underline-offset-4 hover:underline"
          data-testid="glance-open-lmc"
        >
          {isFr ? "Voir tout →" : "See all →"}
        </button>
      </div>}

      {/* Sep 28 2026 · Karine ask · added Linda's Thursday Ottawa card so
          all 3 clinicians surface their next available slots on the same
          board. Grid promoted from 2 → 3 cols on desktop; mobile stays a
          stacked column so each card still hits the readable-line width. */}
      <div className={embedded ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4"}>
        {renderCard({
          providerKey: "karine",
          providerName: "Karine Lauzon",
          title: isFr ? "RN Injectrice · Botox, Filler, Skin" : "Nurse Injector · Botox, Filler, Skin",
          accentBg: "bg-zinc-900",
          accentText: "text-amber-100",
          accentBorder: "border-amber-500/30",
          accentDot: "bg-amber-400",
          chipBg: "bg-amber-500/20",
          chipBorder: "border-amber-400/70",
          chipText: "text-amber-50",
          buttonBg: "bg-amber-600 hover:bg-amber-500",
        })}
        {renderCard({
          providerKey: "linda",
          providerName: "Linda Lafrance",
          title: isFr ? "IP · Ottawa · Jeudis · Botox, Filler, Sculptra" : "NP · Ottawa · Thursdays · Botox, Filler, Sculptra",
          accentBg: "bg-zinc-900",
          accentText: "text-teal-100",
          accentBorder: "border-teal-500/30",
          accentDot: "bg-teal-400",
          chipBg: "bg-teal-500/20",
          chipBorder: "border-teal-400/70",
          chipText: "text-teal-50",
          buttonBg: "bg-teal-600 hover:bg-teal-500",
        })}
        {renderCard({
          providerKey: "tanya",
          providerName: "Tanya Jackson",
          title: isFr ? "Esthéticienne médicale · Ottawa · Laser, soins du visage" : "Medical Aesthetician · Ottawa · Laser, Facials, HydraFacial",
          accentBg: "bg-zinc-900",
          accentText: "text-sky-100",
          accentBorder: "border-sky-500/30",
          accentDot: "bg-sky-400",
          chipBg: "bg-sky-500/20",
          chipBorder: "border-sky-400/70",
          chipText: "text-sky-50",
          buttonBg: "bg-sky-600 hover:bg-sky-500",
        })}
      </div>
    </section>
  );
}
